import { t } from "@lingui/macro";
import {
  Accounting_Centre_Type,
  BillingPlanDocument,
  BillingPlanOptionsDocument,
  type BillingPlanOptionsQuery,
  type BillingPlanOptionsQueryVariables,
  type BillingPlanQuery,
  type BillingPlanQueryVariables,
  UpdateProjectBillingPlanDocument,
  type UpdateProjectBillingPlanMutation,
  type UpdateProjectBillingPlanMutationVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { toCostCentersOptions } from "@src/utils/map-to-options/cost-centers";
import { compact } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { ProjectBillingPlanSettings } from "./billing-plan-settings";
import { ProjectBillingPlanRow } from "./row";

export class ProjectBillingPlan implements BaseStore {
  readonly projectId: string;
  appStore;

  @observable
  private settings: ProjectBillingPlanSettings;

  @observable
  data: ProjectBillingPlanRow[] = [];

  fetcher = new FetchHelper<BillingPlanQuery, BillingPlanQueryVariables>(
    BillingPlanDocument,
  );

  private optionsFetcher = new FetchHelper<
    BillingPlanOptionsQuery,
    BillingPlanOptionsQueryVariables
  >(BillingPlanOptionsDocument);

  @observable.ref
  accountingCenterOptions: IOption[] = [];

  private mutator = new MutationHelper<
    UpdateProjectBillingPlanMutation,
    UpdateProjectBillingPlanMutationVariables
  >(UpdateProjectBillingPlanDocument);

  constructor(
    projectId: string,
    settingsModel: ProjectBillingPlanSettings,
    appStore: AppStore,
  ) {
    makeObservable(this);
    this.settings = settingsModel;
    this.projectId = projectId;
    this.appStore = appStore;
  }

  @computed
  get isFetching(): boolean {
    return this.mutator.isLoading.value || this.fetcher.isLoading.value;
  }

  @computed
  get isFetchingOptions(): boolean {
    return this.optionsFetcher.isLoading.value;
  }

  @action.bound
  setFromQuery(data: BillingPlanQuery) {
    this.data = data.billingPlanBudget.map(
      (item) =>
        new ProjectBillingPlanRow(item, this.settings.period, this.appStore),
    );
  }

  @action.bound
  async fetch() {
    const [data, error] = await this.fetcher.fetch({
      projectId: this.projectId,
      billingItemType: this.settings.billingItemType,
    });

    if (error) {
      this.appStore.UIStore.toast({
        title: t`Something went wrong with fetching billing plan, please try again later.`,
        status: "error",
      });
      return;
    }

    this.setFromQuery(data);
  }

  @action.bound
  async fetchOptions() {
    const [data, error] = await this.optionsFetcher.fetch({
      accountingCentreType: Accounting_Centre_Type.Invoice,
    });

    if (error) return;

    this.accountingCenterOptions = toCostCentersOptions(data.accountingCentres);
  }

  @action.bound
  async update(): Promise<boolean> {
    const [data, errors] = await this.mutator.mutate({
      input: {
        project_id: this.projectId,
        billingPlanBudgets: compact(
          this.data.map(({ mutationInput }) => mutationInput),
        ),
      },
    });

    if (errors) return false;

    this.fetcher.cacheResponse({
      billingPlanBudget: data.updateProjectBillingPlanBudget,
    });
    this.setFromQuery({
      billingPlanBudget: data.updateProjectBillingPlanBudget,
    });

    return true;
  }

  revertToCached() {
    const cached = this.fetcher.getCachedResponse();
    if (!cached) return;
    this.setFromQuery(cached);
  }
}
